import {Vue, Watch} from 'vue-property-decorator';
import {Getter} from 'vuex-class';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import {Data, DateOption} from '@/features/reports/models';
import shortid from 'shortid';
import {ChartType} from '@/features/reports/models/chartType';

export default abstract class BaseReport extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public selectedDateOption!: DateOption;

	public abstract reportId: string;

	public data: Data = { scale: { title: '', labels: [] }, series: [] };

	public chartType: ChartType = ChartType.LINE;

	public loadingError: boolean = false;
	public loadedData: boolean = false;

	public mounted() {
		this.reportId += `-${shortid.generate()}`;
	}

	public get loadedAndReturnedData() {
		return this.loadedData && !this.isDataEmpty();
	}

	public get loadedAndNotReturnedData() {
		return this.loadedData && this.isDataEmpty();
	}

	public get showLineChart() {
		return this.chartType === ChartType.LINE;
	}

	public get showBarChart() {
		return this.chartType === ChartType.BAR;
	}

	public get showPercentageBarChart() {
		return this.chartType === ChartType.PERCENTAGE_BAR;
	}

	@Watch('selectedDateOption', { immediate: true })
	public async onFilterChange(filter: DateOption, previous: DateOption) {
		if (filter && filter === previous) {
			return;
		}

		await this.renderReportData();
	}

	public switchLineChart() {
		return (this.chartType = ChartType.LINE);
	}

	public switchBarChart() {
		return (this.chartType = ChartType.BAR);
	}

	public switchStackedChart() {
		return (this.chartType = ChartType.PERCENTAGE_BAR);
	}

	public async renderReportData() {
		if (!this.isReportCriterionValid()) {
			return;
		}
		this.loadedData = false;
		this.loadingError = false;

		try {
			this.data = await this.loadReportData();
			this.loadedData = true;
		} catch {
			this.loadingError = true;
		} finally {
			this.$emit('load');
		}
	}

	public abstract isReportCriterionValid(): boolean;

	public abstract loadReportData(): Promise<Data>;

	public isDataEmpty(): boolean {
		for (const series of this.data.series) {
			const numberOfPopulatedValuesInSeries = series.data.filter((val) => val > 0).length;
			if (numberOfPopulatedValuesInSeries > 0) {
				return false;
			}
		}

		return true;
	}
}
